export type Brand = {
  name: string;
  url: string;
  tagline: string;
  usp: string;
  voice: string;
  industry: string;
  colors: Record<string, string>;
  fonts: { heading: string; body: string };
  logo: string;
  logoMode: string;
  logoType?: string;
};

export type Idea = {
  id: string;
  category: string;
  funnel: string;
  title: string;
  hookId: string;
  template: string;
  values: Record<string, string>;
  payoff: string;
  cta: string;
  impactScore: number;
  notes?: string;
  /** ISO YYYY-MM-DD — when the idea was added to the bank. */
  createdAt?: string;
  updatedAt?: string;
  releaseLabel?: string;
};

export type FieldDefinition = {
  key: string;
  label: string;
  kind: "text" | "longtext" | "list" | "number" | "ctaKey" | "variant" | "accent" | "media" | "mediaList" | "boolean";
  hint?: string;
  default?: unknown;
  min?: number;
  max?: number;
  options?: string[];
  required?: boolean;
};

export type SceneInputs = {
  text?: string;
  subtext?: string;
  number?: string;
  unit?: string;
  items?: string[];
  /** Generic media URLs (uploaded images / videos). Used by phone-mockup
   *  scenes for swappable per-screen content. */
  media?: string[];
  url?: string;
  variant?: string;
  accent?: "primary" | "secondary" | "accent";
  /** Frei strukturierter Bag — primär Primitive (`text/number/boolean`),
   *  aber für komplexere per-Scene Konfigurationen (z. B. die
   *  per-Slot-Settings von `colorBlockReveal.allyverSlots`) auch Arrays
   *  und Objekte. Wird 1:1 in die Reel-JSON geserialisiert. */
  extras?: Record<string, string | number | boolean | unknown[] | Record<string, unknown> | undefined>;
};

/* ---------- Media uploads ---------- */
export type MediaKind = "image" | "video" | "other";
export type UploadInfo = {
  filename: string;
  url: string;
  mime: string;
  kind: MediaKind;
  size: number;
  uploadedAt: number;
};

export type AutoDurationConfig = {
  source: "items";
  perItem: number;
  padding?: number;
  minSec?: number;
  maxSec?: number;
};

export type SceneMeta = {
  id: string;
  label: string;
  description: string;
  look: "kinetic" | "minimal" | "punchy" | "data" | "mockup";
  category: "intro" | "text" | "data" | "list" | "visual" | "outro" | "partner";
  defaultDurationSec: number;
  defaultInputs: SceneInputs;
  fields: FieldDefinition[];
  variants: string[];
  /** When set, the inspector auto-resizes `durationSec` based on input count. */
  autoDuration?: AutoDurationConfig;
  /** ISO YYYY-MM-DD — when the scene was added to the library. */
  createdAt?: string;
  updatedAt?: string;
  releaseLabel?: string;
};

export type ReelTransitionKind =
  | "none"
  | "crossfade"
  | "wipeLeft"
  | "wipeRight"
  | "slideUp"
  | "slideDown"
  | "slideLeft"
  | "slideRight"
  | "zoomIn"
  | "zoomOut"
  | "blurDissolve"
  | "glideUp"
  | "flash";

export type ReelScene = {
  uid: string;
  sceneId: string;
  /** Length of the visible clip window on the timeline. */
  durationSec: number;
  /**
   * Intrinsic playback duration the scene's animation was authored for.
   * Decouples internal scene timing from the timeline window so resizing a
   * clip trims/pads instead of stretching the animation. Falls back to
   * `durationSec` when omitted. Hold ⌥/Alt while dragging a resize handle
   * to scale this together with the window (stretch mode).
   */
  naturalDurationSec?: number;
  inputs: SceneInputs;
  transition?: { kind: ReelTransitionKind; durationFrames: number };
  /**
   * Absolute start time (in seconds) on the timeline. When undefined, the
   * scene is treated as sequential (legacy behaviour: stacked back-to-back).
   * When defined for ANY scene, the timeline becomes free-form / multi-track.
   */
  fromSec?: number;
  /** Layer index. 0 = base layer; higher numbers render on top. Default 0. */
  track?: number;
  /**
   * Animations-Tempo nur für diesen Clip (Relativ zur Timeline): 1 = Standard.
   * &gt;1 = schneller (mehr Anim-Frames pro Timeline-Frame), &lt;1 = langsamer.
   */
  playbackRate?: number;
};

export type ReelPreset = {
  ideaId: string;
  title: string;
  description: string;
  scenes: ReelScene[];
  createdAt?: string;
  updatedAt?: string;
  releaseLabel?: string;
};

/* ---------- Complete Reel/Story/Carousel templates -------------------------
 *  Bundles `scenes + overlays + format` into one ready-to-shoot vorlage.
 *  Loaded into the Composer via `api.reelTemplate(id)` and a dedicated
 *  hydration path that applies overlays + format up front.
 * ------------------------------------------------------------------------- */
export type ReelTemplateKind = "reel" | "story" | "carousel";
export type ReelTemplateAudience = "b2c" | "b2b" | "affiliate" | "brand";

export type ReelTemplate = {
  id: string;
  kind: ReelTemplateKind;
  title: string;
  description: string;
  tagline?: string;
  format: "9:16" | "1:1" | "16:9";
  audience: ReelTemplateAudience;
  durationSec: number;
  tags: string[];
  scenes: ReelScene[];
  overlays: OverlayLayer[];
  createdAt?: string;
  updatedAt?: string;
  releaseLabel?: string;
};

/* ---------- Free-form overlay layers (text/shape/icon/badge/image) ---------- */

export type OverlayAlign = "left" | "center" | "right";
export type OverlayAccent = "primary" | "secondary" | "accent" | "white" | "black";

/** Fields shared by ALL overlay kinds — kept here so the inspector and
 *  drag handles can operate generically on them. */
export type OverlayBase = {
  uid: string;
  /** Absolute timeline start in seconds. */
  fromSec: number;
  /** Visible duration in seconds. */
  durSec: number;
  /** Layer index — overlays render ABOVE scenes; higher tracks render on top. */
  track: number;
  /** Position of the element CENTER in % of composition (0..100). 50/50 = canvas centre. */
  xPct: number;
  yPct: number;
  /** Uniform scale multiplier (1 = native size). */
  scale: number;
  /** Rotation in degrees. */
  rotate: number;
  /** Opacity 0..1. */
  opacity: number;
  /** Photoshop-style "eye" toggle. Hidden layers are skipped at render time. */
  hidden?: boolean;
  /** Photoshop-style "lock" toggle. Locked layers cannot be selected / moved on canvas. */
  locked?: boolean;
  /** Optional human-friendly layer name shown in the Layers panel. */
  name?: string;
  /** Optional parent group's uid. When set, the layer lives inside the group;
   *  hidden/locked state cascades from the parent at render time. */
  parentUid?: string;
};

export type TextOverlayLayer = OverlayBase & {
  kind: "text";
  text: string;
  /** Font size in COMPOSITION pixels (so it scales with the format). */
  fontSize: number;
  /** Font weight 100..900. */
  weight: number;
  /** Token ("primary" / "accent" / …) OR a literal CSS color (#hex / rgba). */
  color: OverlayAccent | string;
  align: OverlayAlign;
  /** Optional drop shadow for legibility on busy backgrounds. */
  shadow?: boolean;
  /** Optional letter-spacing in em (default 0). */
  letterSpacing?: number;
  /** Optional max width in % of composition (wrap behavior). */
  maxWidthPct?: number;
};

export type ShapeKind = "rect" | "pill" | "circle" | "star" | "arrow" | "triangle";
export type ShapeOverlayLayer = OverlayBase & {
  kind: "shape";
  shape: ShapeKind;
  /** Width in % of composition. */
  widthPct: number;
  /** Height in % of composition. */
  heightPct: number;
  fill?: OverlayAccent | string;
  stroke?: OverlayAccent | string;
  strokeWidth?: number;
  /** Border-radius in px (only for rect/pill). */
  radius?: number;
};

export type IconOverlayLayer = OverlayBase & {
  kind: "icon";
  /** Lucide-style icon name, e.g. "star", "heart", "check". */
  icon: string;
  /** Size in % of composition WIDTH. */
  sizePct: number;
  color?: OverlayAccent | string;
  strokeWidth?: number;
};

export type BadgeOverlayLayer = OverlayBase & {
  kind: "badge";
  /** Pill-shaped label with optional leading icon. */
  text: string;
  icon?: string;
  fontSize: number;
  fill?: OverlayAccent | string;
  textColor?: OverlayAccent | string;
};

export type ImageOverlayLayer = OverlayBase & {
  kind: "image";
  src: string;
  widthPct: number;
  heightPct: number;
  fit?: "cover" | "contain";
  radius?: number;
};

/** Photoshop-style folder. Has no visual presence; its `hidden` / `locked`
 *  cascade to every child layer that points at it via `parentUid`. */
export type GroupOverlayLayer = OverlayBase & {
  kind: "group";
  /** UI-only: whether the group is expanded in the LayersPanel. */
  expanded?: boolean;
};

export type OverlayLayer =
  | TextOverlayLayer
  | ShapeOverlayLayer
  | IconOverlayLayer
  | BadgeOverlayLayer
  | ImageOverlayLayer
  | GroupOverlayLayer;

export type OverlayKind = OverlayLayer["kind"];

export type TemplateMeta = {
  id: string;
  label: string;
  category: string;
  description: string;
  defaultDurationSeconds: number;
  fields: FieldDefinition[];
  defaultProps: Record<string, unknown>;
};

export type Category = {
  id: string;
  label: string;
  funnel: string;
  description: string;
  goodFor: string[];
};

export type RenderTarget = { language: string; format: string; path: string; url: string };

export type RenderProgress = {
  status: "queued" | "rendering" | "done" | "failed";
  currentTarget?: { language: string; format: string };
  progress: number;
  startedAt?: number;
  finishedAt?: number;
  error?: string;
  outputs: RenderTarget[];
};

export type RenderSettings = {
  /** Max. gleichzeitig laufende Renders. */
  renderConcurrency: number;
  /** Obergrenze, die die UI anbieten darf. */
  maxRenderConcurrency: number;
  /** Aktuell laufende Renders. */
  active?: number;
  /** Auf einen freien Slot wartende Renders. */
  waiting?: number;
};

export type QueueItem = {
  id: string;
  createdAt: string;
  updatedAt: string;
  kind?: "template" | "reel";
  template: string;
  title?: string;
  category: string;
  hookId?: string;
  ideaId?: string;
  props: Record<string, Record<string, unknown>>;
  scenes?: ReelScene[];
  watermark?: WatermarkConfig;
  music?: MusicConfig;
  voiceover?: VoiceoverConfig;
  overlays?: OverlayLayer[];
  sfxEvents?: SfxEvent[];
  sfxConfig?: SfxConfig;
  safeZone?: { enabled: boolean; platform: "tiktok" | "reels" | "shorts" | "stories" | "general" };
  theme?: "dark" | "light";
  languages: string[];
  formats: string[];
  status: "pending" | "rendering" | "done" | "failed";
  outputs: Array<{ language: string; format: string; path: string; url: string }>;
  runtime?: RenderProgress;
  error?: string;
};

/* =====================================================================
 * Workspace types — persisted reels with status, notes, change-requests
 * ===================================================================== */
export type WorkspaceStatus =
  | "draft"
  | "in_progress"
  | "approval_needed"
  | "approved"
  | "rejected";

export type WatermarkPosition = "tl" | "tr" | "bl" | "br";

export type WatermarkConfig = {
  enabled: boolean;
  position: WatermarkPosition;
  opacity: number;
  size: number;
  paddingPx: number;
  /** Optional fine-tune offsets in % of canvas width/height. */
  offsetX?: number; // -50..+50
  offsetY?: number; // -50..+50
  /**
   * Optional ABSOLUTE position (Mittelpunkt des Logos in % der Canvas).
   * Wenn beide gesetzt sind, gewinnen sie über `position` + `offsetX/Y`.
   * Wird vom Drag-Handle gesetzt → Logo komplett frei platzierbar.
   */
  xPct?: number; // 0..100
  yPct?: number; // 0..100
  showOnAllScenes: boolean;
};

/**
 * Background music configuration. Supports both built-in beds (`bed`) AND
 * custom uploads from the audio library (`assetId` + `src`). When `src` is
 * set, the renderer plays that file directly instead of looking up a bed.
 *
 * The new optional fields turn the music track into a full timeline clip so
 * it can be re-positioned, trimmed, faded and looped just like a video clip.
 */
export type MusicConfig = {
  /** Built-in bed key. Mutually exclusive with `src`. */
  bed?: "ambient" | "pulse" | "riser";
  /** Custom audio URL — typically a file from the audio library. */
  src?: string;
  /** Audio-library id (so the UI can render the asset chip / re-load). */
  assetId?: string;
  /** Friendly label shown in the timeline bar / inspector. */
  label?: string;
  /** 0..1, default 0.18. */
  volume: number;
  /** Master toggle — false → silent. */
  enabled: boolean;
  /** Absolute timeline start (sec). Defaults to 0. */
  fromSec?: number;
  /** Visible bar length on the timeline (sec). When omitted the music plays
   *  for the full reel duration (loop-friendly). */
  durSec?: number;
  /** Where in the source file playback begins (sec). 0 = file start. */
  trimStartSec?: number;
  /** Fade-in length in seconds. Default 0. */
  fadeInSec?: number;
  /** Fade-out length in seconds. Default 0. */
  fadeOutSec?: number;
  /** Loop the audio if the bar is longer than the file. Default true for
   *  beds, false for custom uploads. */
  loop?: boolean;
};

/**
 * Voiceover-Spur — eine pro Reel. User lädt eine Sprachdatei hoch und
 * platziert / trimmt / fadet sie auf der Timeline (analog zur Music-Spur,
 * aber mit eigener Lautstärke- + Mute-Steuerung und Schnitt-Funktion am
 * Anfang via `trimStartSec`).
 *
 *  - `src` / `assetId`: Datei aus der Audio-Library oder freie URL.
 *  - `fromSec`/`durSec`: Position + sichtbare Bar-Länge auf der Timeline.
 *  - `trimStartSec`: Wo IM ORIGINAL die Wiedergabe startet (z. B. Aufnahme
 *    ist 30s lang, soll aber erst ab Sekunde 5 abgespielt werden).
 *  - `fadeInSec`/`fadeOutSec`: Sanftes Ein-/Ausblenden.
 *  - `volume`: 0..1 (default 1.0).
 *  - `enabled`: Master-Toggle.
 */
/**
 * Ein einzelnes Schnitt-Stück der Voiceover-Spur. Mehrere Segmente teilen
 * sich Quelle/Volume/Label, sind aber unabhängig auf der Timeline platziert
 * und getrimmt — dadurch lässt sich eine Aufnahme via Blade-Tool in mehrere
 * Stücke zerschneiden und z. B. um Pausen oder bestimmte Wörter herum
 * gruppieren.
 */
export type VoiceoverSegment = {
  id: string;
  fromSec: number;
  durSec: number;
  trimStartSec: number;
  fadeInSec?: number;
  fadeOutSec?: number;
  /** Optionaler eigener `src`. Fehlend → Segment nutzt `VoiceoverConfig.src`. */
  src?: string;
  assetId?: string;
};

export type VoiceoverConfig = {
  src?: string;
  assetId?: string;
  label?: string;
  volume: number;
  enabled: boolean;
  /**
   * Schnitt-Segmente der Voiceover-Spur. Wenn vorhanden + nicht leer →
   * Engine baut ein eigenes <audio>-Element pro Eintrag. Wenn fehlend
   * fallen wir auf das Legacy-Single-Clip-Layout (`fromSec/durSec/...`)
   * zurück, sodass alte Reels weiterhin laden.
   */
  segments?: VoiceoverSegment[];
  fromSec?: number;
  durSec?: number;
  trimStartSec?: number;
  fadeInSec?: number;
  fadeOutSec?: number;
};

/** Verfügbare SFX-Sounds — muss mit `SfxName` aus `src/sfx/SfxBank.tsx` synchron bleiben. */
export type SfxName =
  | "woosh"
  | "swoosh"
  | "impact"
  | "click"
  | "ding"
  | "thud"
  | "pop"
  | "riser"
  | "shimmer"
  | "tap"
  | "tick"
  | "notify"
  | "glass"
  | "magic"
  | "sub"
  | "transition"
  | "sfx495"
  | "sfx503"
  | "sfx505"
  | "sfx676";

/**
 * Unabhängiges Sound-Effekt-Element auf der Timeline. Kann frei verschoben,
 * gestreckt und gelöscht werden — nicht an einen Clip gebunden.
 *
 * `fromSec` = absolute Startzeit im Reel.
 * `durSec` = sichtbare Bar-Länge auf der Timeline. Audio spielt bei `fromSec`,
 * läuft bis zum Ende der Datei oder bis `durSec` (was zuerst kommt).
 *
 * Optional `src` / `assetId` lassen einen aus der Audio-Library geladenen
 * Custom-Sound abspielen statt eines der mitgelieferten `SfxName`-Effekte.
 * Wenn `src` gesetzt ist, wird `sound` ignoriert (bzw. dient nur noch als
 * Fallback-Tag).
 */
export type SfxEvent = {
  uid: string;
  fromSec: number;
  durSec: number;
  sound: SfxName;
  /** Custom audio URL (überschreibt `sound`). */
  src?: string;
  /** Audio-library id, wenn der Sound aus der Library kommt. */
  assetId?: string;
  /** 0..1 — default 0.6. */
  volume?: number;
  /** Pitch playback rate (1 = normal, 0.5 = octave down, 2 = octave up). */
  rate?: number;
  /** Wo im Quell-File die Wiedergabe startet (sec). Default 0. */
  trimStartSec?: number;
  /** Fade-in (sec). Default 0. */
  fadeInSec?: number;
  /** Fade-out (sec). Default 0. */
  fadeOutSec?: number;
  /** Optionaler Eigenname für die Bar (sonst Sound-Key). */
  label?: string;
  /** Wenn vom Inspector-Auto-SFX einer Scene generiert: die Scene-UID, zu
   *  der dieser Beat gehört. Erlaubt es, alle Auto-SFX einer Scene auf
   *  einmal wieder zu entfernen. Manuell erstellte / verschobene Events
   *  haben dieses Feld nicht. */
  sceneUid?: string;
};

/* ---------- Audio Library (uploaded music + SFX + Voiceover) ---------- */
export type AudioAssetKind = "music" | "sfx" | "voiceover";
export type AudioAsset = {
  id: string;
  filename: string;
  url: string;
  displayName: string;
  kind: AudioAssetKind;
  mime: string;
  size: number;
  durationSec: number;
  uploadedAt: number;
  tags?: string[];
  favorite?: boolean;
  source?: "upload" | "elevenlabs";
};

// ---- ElevenLabs (Vertonung) ----
export type ElevenVoice = {
  voiceId: string;
  name: string;
  category: string;
  previewUrl?: string;
  description?: string;
  labels?: Record<string, string>;
};

export type ElevenVoicesResponse = {
  own: ElevenVoice[];
  library: ElevenVoice[];
  favorites: string[];
};

export type AutoScoreSegment = {
  sceneUid: string;
  fromSec: number;
  sceneDurSec: number;
  src: string;
  assetId: string;
  label: string;
  text: string;
};

export type WorkspaceNote = {
  id: string;
  text: string;
  author?: string;
  createdAt: string;
};

export type WorkspaceChangeRequest = {
  id: string;
  text: string;
  done: boolean;
  createdAt: string;
  doneAt?: string;
};

/**
 * Reel-weite SFX-Master (Volume + Mute) — gilt für die Timeline-SFX UND
 * für die in den Szenen eingebetteten Vorlagen-SFX. Default (kein Feld
 * gesetzt) = volle Lautstärke, nicht stumm.
 */
export type SfxConfig = {
  /** 0..1 — Master-Multiplier. Default 1. */
  volume: number;
  /** true → alle SFX werden auf 0 gezogen (kein Re-Mount der Audio-Tags). */
  muted: boolean;
};

export type WorkspaceReel = {
  id: string;
  title: string;
  status: WorkspaceStatus;
  ideaId?: string;
  format: "9:16" | "1:1" | "16:9";
  /** Render-Theme ("dark" | "light"). Default: CI-Theme aus ci.json. */
  theme?: "dark" | "light";
  scenes: ReelScene[];
  /** Free-form overlay layers (text/etc) drawn over the scene composition. */
  overlays?: OverlayLayer[];
  /** Unabhängige Sound-Effekt-Elemente auf der Timeline. */
  sfxEvents?: SfxEvent[];
  /** Reel-weite SFX-Master-Einstellungen — analog zu `music`. */
  sfxConfig?: SfxConfig;
  watermark?: WatermarkConfig;
  music?: MusicConfig;
  /** Voiceover-Spur (vom User hochgeladene Sprachaufnahme). */
  voiceover?: VoiceoverConfig;
  notes: WorkspaceNote[];
  changeRequests: WorkspaceChangeRequest[];
  createdAt: string;
  updatedAt: string;
  description?: string;
};

export type WorkspaceReelLean = {
  id: string;
  title: string;
  status: WorkspaceStatus;
  ideaId?: string;
  format: "9:16" | "1:1" | "16:9";
  sceneCount: number;
  noteCount: number;
  crOpen: number;
  crTotal: number;
  createdAt: string;
  updatedAt: string;
};

/* =====================================================================
 * Partner-Link ingestion — paste URL, parsed merchant/products payload
 * ===================================================================== */

export type PartnerItemKind =
  | "Produkt"
  | "Dienstleistung"
  | "Inserat"
  | "Event"
  | "Speisekarte"
  | "Unterkunft"
  | "Transport"
  | "Sonstiges";

export type PartnerItem = {
  name: string;
  url?: string;
  priceLabel?: string;
  priceValue?: number;
  priceCurrency?: string;
  imageUrl?: string;
  kind: PartnerItemKind;
};

export type PartnerType =
  | "Händler"
  | "Dienstleister"
  | "Restaurant"
  | "Hotel"
  | "Taxi"
  | "Event"
  | "Inserent"
  | "Partner";

export type PartnerData = {
  sourceUrl: string;
  displayUrl: string;
  slug?: string;
  name: string;
  description?: string;
  tagline?: string;
  location?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  category?: string;
  type?: PartnerType;
  logoUrl?: string;
  items: PartnerItem[];
  ratingLabel?: string;
};

export type ProductData = {
  sourceUrl: string;
  displayUrl: string;
  slug?: string;
  name: string;
  priceLabel?: string;
  priceValue?: number;
  priceCurrency?: string;
  description?: string;
  sellerName?: string;
  location?: string;
  city?: string;
  postalCode?: string;
  imageUrl?: string;
  categoryTag?: string;
  shopUrl?: string;
};

export type PartnerApplyReport = {
  scenesTouched: number;
  scenes: Array<{ uid: string; sceneId: string; changedKeys: string[] }>;
  warnings: string[];
};

/* =====================================================================
 * Workflow types — Idea pool, storyboards, plan, deployments, agent runs
 * ===================================================================== */

export type IdeaPoolStatus =
  | "new"
  | "approved"
  | "in_storyboard"
  | "done"
  | "rejected";

export type IdeaPoolAudience = "b2c" | "b2b" | "affiliate" | "brand" | "any";
export type IdeaPoolFunnel = "awareness" | "interest" | "decision" | "action";
export type IdeaPoolGeneratedBy = "ai" | "human" | "agent";

export type PoolIdea = {
  id: string;
  title: string;
  hook: string;
  payoff: string;
  cta?: string;
  audience: IdeaPoolAudience;
  funnel: IdeaPoolFunnel;
  topic?: string;
  tags?: string[];
  sourceUrls?: string[];
  impactScore?: 1 | 2 | 3 | 4 | 5;
  status: IdeaPoolStatus;
  generatedBy: IdeaPoolGeneratedBy;
  agentRunId?: string;
  storyboardId?: string;
  reelId?: string;
  createdAt: string;
  updatedAt: string;
};

export type StoryboardStatus = "draft" | "ready" | "in_composer" | "done";
export type StoryboardFormat = "9:16" | "1:1" | "16:9";

export type StoryboardBeat = {
  uid: string;
  sceneId: string;
  text: string;
  subtext?: string;
  items?: string[];
  durationSec: number;
  note?: string;
};

export type Storyboard = {
  id: string;
  title: string;
  ideaId?: string;
  format: StoryboardFormat;
  durationSec: number;
  beats: StoryboardBeat[];
  status: StoryboardStatus;
  reelId?: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
};

export type Platform = "facebook" | "instagram" | "x" | "youtube" | "threads" | "tiktok";

export const ALL_PLATFORMS: Platform[] = [
  "facebook",
  "instagram",
  "x",
  "youtube",
  "threads",
  "tiktok",
];

export const PLATFORM_META: Record<Platform, { label: string; color: string; icon: string }> = {
  facebook:  { label: "Facebook",  color: "#1877f2", icon: "📘" },
  instagram: { label: "Instagram", color: "#e1306c", icon: "📷" },
  x:         { label: "X / Twitter", color: "#0f1419", icon: "✖" },
  youtube:   { label: "YouTube",   color: "#ff0000", icon: "▶" },
  threads:   { label: "Threads",   color: "#000000", icon: "@" },
  tiktok:    { label: "TikTok",    color: "#010101", icon: "🎵" },
};

export type PlanEntry = {
  id: string;
  dateISO: string;
  time?: string;
  ideaId?: string;
  storyboardId?: string;
  reelId?: string;
  channels: Platform[];
  note?: string;
  createdAt: string;
  updatedAt: string;
};

export type DeploymentStatus = "planned" | "queued" | "sent" | "failed";

export type PlatformPlan = {
  status: DeploymentStatus;
  caption: string;
  hashtags: string[];
  scheduledAt?: string;
  externalId?: string;
  error?: string;
  updatedAt: string;
};

export type ReelDeployment = {
  reelId: string;
  title?: string;
  platforms: Partial<Record<Platform, PlatformPlan>>;
  createdAt: string;
  updatedAt: string;
};

export type DispatchResult = {
  reelId: string;
  intents: Array<{ platform: Platform; status: DeploymentStatus; intentUrl?: string }>;
};

export const POOL_STATUS_META: Record<IdeaPoolStatus, { label: string; color: string }> = {
  new:           { label: "Neu",           color: "#94a3b8" },
  approved:      { label: "Freigegeben",   color: "#22c55e" },
  in_storyboard: { label: "Im Storyboard", color: "#3b82f6" },
  done:          { label: "Erledigt",      color: "#16a34a" },
  rejected:      { label: "Abgelehnt",     color: "#ef4444" },
};

export const POOL_ALL_STATUSES: IdeaPoolStatus[] = [
  "new",
  "approved",
  "in_storyboard",
  "done",
  "rejected",
];

export const STORYBOARD_STATUS_META: Record<StoryboardStatus, { label: string; color: string }> = {
  draft:       { label: "Entwurf",     color: "#94a3b8" },
  ready:       { label: "Bereit",      color: "#22c55e" },
  in_composer: { label: "Im Composer", color: "#3b82f6" },
  done:        { label: "Fertig",      color: "#16a34a" },
};

export const STORYBOARD_ALL_STATUSES: StoryboardStatus[] = [
  "draft",
  "ready",
  "in_composer",
  "done",
];

const base = "";

// Wird von der App registriert: läuft eine Session ab, antwortet der Server mit
// 401 → wir benachrichtigen die App, die dann den Login-Screen zeigt.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}
function noteStatus(p: string, status: number): void {
  // Auth-Endpoints selbst nicht als "Session abgelaufen" werten.
  if (status === 401 && !p.startsWith("/api/auth/")) onUnauthorized?.();
}

async function get<T>(p: string): Promise<T> {
  const r = await fetch(`${base}${p}`, { credentials: "same-origin" });
  if (!r.ok) { noteStatus(p, r.status); throw new Error(`${r.status} ${r.statusText}`); }
  return (await r.json()) as T;
}

async function post<T>(p: string, body?: unknown): Promise<T> {
  const r = await fetch(`${base}${p}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) { noteStatus(p, r.status); throw new Error(`${r.status} ${r.statusText}: ${await r.text()}`); }
  return (await r.json()) as T;
}

async function del<T>(p: string): Promise<T> {
  const r = await fetch(`${base}${p}`, { method: "DELETE", credentials: "same-origin" });
  if (!r.ok) { noteStatus(p, r.status); throw new Error(`${r.status} ${r.statusText}`); }
  return (await r.json()) as T;
}

async function patch<T>(p: string, body?: unknown): Promise<T> {
  const r = await fetch(`${base}${p}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) { noteStatus(p, r.status); throw new Error(`${r.status} ${r.statusText}: ${await r.text()}`); }
  return (await r.json()) as T;
}

export const STATUS_META: Record<WorkspaceStatus, { label: string; color: string }> = {
  draft:           { label: "Entwurf",         color: "#94a3b8" },
  in_progress:     { label: "In Bearbeitung",  color: "#3b82f6" },
  approval_needed: { label: "Approval nötig",  color: "#f59e0b" },
  approved:        { label: "Approved",        color: "#22c55e" },
  rejected:        { label: "Abgelehnt",       color: "#ef4444" },
};

export const ALL_STATUSES: WorkspaceStatus[] = [
  "draft",
  "in_progress",
  "approval_needed",
  "approved",
  "rejected",
];

export type AuthStatus = { required: boolean; authenticated: boolean };
export type UserRole = "admin" | "editor";
export type Me = { username: string; role: UserRole; isAdmin: boolean };
export type ManagedUser = {
  id: string;
  username: string;
  active: boolean;
  role: UserRole;
  createdAt: string;
  updatedAt: string;
  isSelf?: boolean;
};

export const api = {
  authStatus: () => get<AuthStatus>("/api/auth/status"),
  authMe: () => get<Me>("/api/auth/me"),
  authLogin: (password: string, username?: string) =>
    post<{ ok: boolean }>("/api/auth/login", { password, username }),
  authLogout: () => post<{ ok: boolean }>("/api/auth/logout"),
  usersList: () => get<{ users: ManagedUser[]; envAdmin: string | null }>("/api/users"),
  userCreate: (username: string, password: string, role: UserRole) =>
    post<{ user: ManagedUser }>("/api/users", { username, password, role }),
  userUpdate: (id: string, body: { active?: boolean; role?: UserRole }) =>
    patch<{ user: ManagedUser }>(`/api/users/${id}`, body),
  userSetPassword: (id: string, password: string) =>
    post<{ ok: boolean }>(`/api/users/${id}/password`, { password }),
  userDelete: (id: string) => del<{ ok: boolean }>(`/api/users/${id}`),
  brand: () => get<Brand>("/api/brand"),
  templates: () => get<TemplateMeta[]>("/api/templates"),
  categories: () => get<Category[]>("/api/categories"),
  ideas: () => get<Idea[]>("/api/ideas"),
  formats: () => get<string[]>("/api/formats"),
  locales: () => get<string[]>("/api/locales"),
  scenes: () => get<SceneMeta[]>("/api/scenes"),
  reelPresets: () => get<ReelPreset[]>("/api/reel-presets"),
  reelPreset: (ideaId: string) => get<ReelPreset>(`/api/reel-presets/${ideaId}`),
  reelTemplates: () => get<ReelTemplate[]>("/api/reel-templates"),
  reelTemplate: (id: string) => get<ReelTemplate>(`/api/reel-templates/${id}`),
  queue: () => get<{ version: number; items: QueueItem[] }>("/api/queue"),
  addToQueue: (body: {
    template: string;
    category?: string;
    hookId?: string;
    ideaId?: string;
    props: Record<string, Record<string, unknown>>;
    languages: string[];
    formats: string[];
    renderNow?: boolean;
  }) => post<{ id: string; item: QueueItem }>("/api/queue", body),
  addReelToQueue: (body: {
    title?: string;
    category?: string;
    ideaId?: string;
    scenes: ReelScene[];
    languages: string[];
    formats: string[];
    renderNow?: boolean;
    watermark?: WatermarkConfig;
    music?: MusicConfig;
    voiceover?: VoiceoverConfig;
    overlays?: OverlayLayer[];
    sfxEvents?: SfxEvent[];
    sfxConfig?: SfxConfig;
    safeZone?: { enabled: boolean; platform: "tiktok" | "reels" | "shorts" | "stories" | "general" };
    theme?: "dark" | "light";
  }) => post<{ id: string; item: QueueItem }>("/api/queue", { ...body, kind: "reel" }),
  removeFromQueue: (id: string) => del<{ ok: boolean }>(`/api/queue/${id}`),
  clearQueue: () => post<{ ok: boolean }>("/api/queue/clear"),
  /** Sammel-Aktion auf bestehende Queue-Items: mehrere rendern oder entfernen. */
  queueBatch: (ids: string[], action: "render" | "remove") =>
    post<{ ok: boolean; action: string; count: number; started?: string[] }>("/api/queue/batch", { ids, action }),
  startRender: (id: string) => post<{ ok: boolean }>(`/api/render/${id}`),
  renderStatus: () => get<Record<string, RenderProgress>>("/api/render/status"),
  /** Sammel-Render mehrerer Workspace-Reels (sequenziell im Hintergrund). */
  renderBatch: (ids: string[], theme: "reel" | "dark" | "light" | "both" = "reel") =>
    post<{
      ok: boolean;
      created: { reelId: string; queueId: string; title: string; theme: string }[];
      missing: string[];
    }>("/api/render/batch", { ids, theme }),
  /** Download-URL für ein ZIP mit allen fertigen Render-Outputs (optional gefiltert). */
  downloadAllOutputsUrl: (ids?: string[]) =>
    `${base}/api/outputs/download-all${ids && ids.length ? `?ids=${encodeURIComponent(ids.join(","))}` : ""}`,
  /** Render-Einstellungen lesen (u. a. max. gleichzeitige Renders). */
  getSettings: () => get<RenderSettings>("/api/settings"),
  /** Render-Parallelität setzen (wird serverseitig auf 1..max geklemmt). */
  setRenderConcurrency: (renderConcurrency: number) =>
    post<RenderSettings>("/api/settings", { renderConcurrency }),
  aiStatus: () => get<{ enabled: boolean; provider: string; model: string }>("/api/ai/status"),
  aiGenerateReel: (body: { prompt: string; targetSec?: number; locale?: string }) =>
    post<{ title: string; scenes: ReelScene[]; modelUsed: string }>("/api/ai/generate-reel", body),

  // ---- Workspace ----
  workspaceList: () => get<WorkspaceReelLean[]>("/api/workspace/reels"),
  workspaceGet: (id: string) => get<WorkspaceReel>(`/api/workspace/reels/${id}`),
  workspaceSave: (body: Partial<WorkspaceReel> & { scenes: ReelScene[] }) =>
    post<WorkspaceReel>("/api/workspace/reels", body),
  workspaceDelete: (id: string) => del<{ ok: boolean }>(`/api/workspace/reels/${id}`),
  workspaceBatchDelete: (ids: string[]) =>
    post<{ ok: boolean; deleted: number }>("/api/workspace/reels/batch-delete", { ids }),
  workspaceSetStatus: (id: string, status: WorkspaceStatus) =>
    patch<WorkspaceReel>(`/api/workspace/reels/${id}/status`, { status }),
  workspaceAddNote: (id: string, text: string, author?: string) =>
    post<WorkspaceReel>(`/api/workspace/reels/${id}/notes`, { text, author }),
  workspaceDeleteNote: (id: string, noteId: string) =>
    del<WorkspaceReel>(`/api/workspace/reels/${id}/notes/${noteId}`),
  workspaceAddCR: (id: string, text: string) =>
    post<WorkspaceReel>(`/api/workspace/reels/${id}/change-requests`, { text }),
  workspaceToggleCR: (id: string, crId: string, done: boolean) =>
    patch<WorkspaceReel>(`/api/workspace/reels/${id}/change-requests/${crId}`, { done }),
  workspaceDeleteCR: (id: string, crId: string) =>
    del<WorkspaceReel>(`/api/workspace/reels/${id}/change-requests/${crId}`),

  // ---- Media uploads (phone-mockup screens, etc.) ----
  uploadsList: () => get<{ items: UploadInfo[] }>("/api/uploads"),
  uploadsDelete: (filename: string) => del<{ ok: boolean }>(`/api/uploads/${encodeURIComponent(filename)}`),
  uploadFiles: async (files: FileList | File[]): Promise<{ uploaded: UploadInfo[] }> => {
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f, f.name));
    const r = await fetch(`${base}/api/uploads`, { method: "POST", body: fd });
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}: ${await r.text()}`);
    return (await r.json()) as { uploaded: UploadInfo[] };
  },

  // ---- Audio library (persistent across reels) ----
  audioList: () => get<{ items: AudioAsset[] }>("/api/audio"),
  audioUpload: async (
    files: FileList | File[],
    kind: AudioAssetKind = "music",
  ): Promise<{ uploaded: AudioAsset[] }> => {
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f, f.name));
    fd.append("kind", kind);
    const r = await fetch(`${base}/api/audio`, { method: "POST", body: fd });
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}: ${await r.text()}`);
    return (await r.json()) as { uploaded: AudioAsset[] };
  },
  audioPatch: (id: string, body: Partial<Pick<AudioAsset, "displayName" | "kind" | "tags" | "durationSec" | "favorite">>) =>
    patch<AudioAsset>(`/api/audio/${id}`, body),
  audioDelete: (id: string) => del<{ ok: boolean }>(`/api/audio/${id}`),

  // ---- ElevenLabs (Vertonung) ----
  elevenStatus: () => get<{ enabled: boolean }>("/api/elevenlabs/status"),
  elevenVoices: () => get<ElevenVoicesResponse>("/api/elevenlabs/voices"),
  elevenSetFavorites: (voiceIds: string[]) =>
    post<{ voiceIds: string[] }>("/api/elevenlabs/favorites", { voiceIds }),
  elevenTts: (body: { text: string; voiceId: string; modelId?: string; name?: string }) =>
    post<{ asset: AudioAsset }>("/api/elevenlabs/tts", body),
  elevenVoiceChanger: async (
    audio: Blob,
    voiceId: string,
    opts?: { filename?: string; name?: string; removeBackgroundNoise?: boolean },
  ): Promise<{ asset: AudioAsset }> => {
    const fd = new FormData();
    fd.append("file", audio, opts?.filename ?? "recording.webm");
    fd.append("voiceId", voiceId);
    if (opts?.name) fd.append("name", opts.name);
    if (opts?.removeBackgroundNoise) fd.append("removeBackgroundNoise", "true");
    const r = await fetch(`${base}/api/elevenlabs/voice-changer`, {
      method: "POST",
      credentials: "same-origin",
      body: fd,
    });
    if (!r.ok) {
      noteStatus("/api/elevenlabs/voice-changer", r.status);
      let msg = `${r.status} ${r.statusText}`;
      try {
        const j = (await r.json()) as { error?: string };
        if (j.error) msg = j.error;
      } catch { /* ignore */ }
      throw new Error(msg);
    }
    return (await r.json()) as { asset: AudioAsset };
  },
  elevenMusic: (body: { prompt: string; musicLengthMs?: number; forceInstrumental?: boolean; name?: string }) =>
    post<{ asset: AudioAsset }>("/api/elevenlabs/music", body),
  elevenSfx: (body: {
    text: string;
    durationSeconds?: number;
    promptInfluence?: number;
    loop?: boolean;
    name?: string;
  }) => post<{ asset: AudioAsset }>("/api/elevenlabs/sfx", body),
  elevenAutoScore: (body: {
    scenes: { uid: string; text?: string; fromSec: number; durSec: number }[];
    mode: "scene-text" | "ai-script";
    voiceId: string;
    locale?: string;
  }) => post<{ segments: AutoScoreSegment[]; voiceId: string; mode: string }>("/api/elevenlabs/auto-score", body),

  // ---- Partner-Link ingestion (paste URL → swap reel content) ----
  partnerExtract: (url: string, opts?: { mirror?: boolean }) =>
    get<PartnerData>(
      `/api/partner-extract?url=${encodeURIComponent(url)}${opts?.mirror ? "&mirror=1" : ""}`,
    ),
  partnerProductExtract: (url: string, opts?: { mirror?: boolean }) =>
    get<ProductData>(
      `/api/partner-product-extract?url=${encodeURIComponent(url)}${opts?.mirror ? "&mirror=1" : ""}`,
    ),
  partnerApplyToReel: (
    reelId: string,
    body: { url?: string; partner?: PartnerData },
  ) =>
    post<{ reel: WorkspaceReel; partner: PartnerData; report: PartnerApplyReport }>(
      `/api/workspace/reels/${reelId}/apply-partner`,
      body,
    ),
  partnerCloneReel: (
    reelId: string,
    body: { url?: string; partner?: PartnerData; cloneAs?: string },
  ) =>
    post<{ reel: WorkspaceReel; partner: PartnerData; report: PartnerApplyReport }>(
      `/api/workspace/reels/${reelId}/clone-with-partner`,
      body,
    ),

  // ---- Idea Pool ----
  ideasPoolList: () => get<PoolIdea[]>("/api/ideas-pool"),
  ideasPoolGet: (id: string) => get<PoolIdea>(`/api/ideas-pool/${id}`),
  ideasPoolSave: (body: Partial<PoolIdea>) => post<PoolIdea>("/api/ideas-pool", body),
  ideasPoolPatch: (id: string, body: Partial<PoolIdea>) =>
    patch<PoolIdea>(`/api/ideas-pool/${id}`, body),
  ideasPoolSetStatus: (id: string, status: IdeaPoolStatus) =>
    patch<PoolIdea>(`/api/ideas-pool/${id}/status`, { status }),
  ideasPoolDelete: (id: string) => del<{ ok: boolean }>(`/api/ideas-pool/${id}`),
  ideasPoolGenerate: (body: {
    count?: number;
    audience?: IdeaPoolAudience;
    topics?: string[];
    prompt?: string;
    research?: boolean;
    liveWeb?: boolean;
  }) => post<{ ideas: PoolIdea[]; modelUsed: string }>("/api/ideas-pool/generate", body),
  ideasPoolToStoryboard: (id: string, body?: { format?: StoryboardFormat; targetSec?: number }) =>
    post<Storyboard>(`/api/ideas-pool/${id}/to-storyboard`, body ?? {}),

  // ---- Storyboards ----
  storyboardsList: () => get<Storyboard[]>("/api/storyboards"),
  storyboardsGet: (id: string) => get<Storyboard>(`/api/storyboards/${id}`),
  storyboardsSave: (body: Partial<Storyboard>) => post<Storyboard>("/api/storyboards", body),
  storyboardsPatch: (id: string, body: Partial<Storyboard>) =>
    patch<Storyboard>(`/api/storyboards/${id}`, body),
  storyboardsSetStatus: (id: string, status: StoryboardStatus) =>
    patch<Storyboard>(`/api/storyboards/${id}/status`, { status }),
  storyboardsDelete: (id: string) => del<{ ok: boolean }>(`/api/storyboards/${id}`),
  storyboardsToReel: (id: string) =>
    post<{ storyboard: Storyboard; reel: WorkspaceReel }>(`/api/storyboards/${id}/to-reel`),

  // ---- Plan / Calendar ----
  planList: () => get<PlanEntry[]>("/api/plan"),
  planSave: (body: Partial<PlanEntry>) => post<PlanEntry>("/api/plan", body),
  planPatch: (id: string, body: Partial<PlanEntry>) =>
    patch<PlanEntry>(`/api/plan/${id}`, body),
  planDelete: (id: string) => del<{ ok: boolean }>(`/api/plan/${id}`),

  // ---- Deployments ----
  deploymentsList: () => get<ReelDeployment[]>("/api/deployments"),
  deploymentsGet: (reelId: string) => get<ReelDeployment>(`/api/deployments/${reelId}`),
  deploymentsSave: (reelId: string, body: ReelDeployment) =>
    fetch(`/api/deployments/${reelId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}: ${await r.text()}`);
      return (await r.json()) as ReelDeployment;
    }),
  deploymentsSetPlatform: (reelId: string, platform: Platform, body: Partial<PlatformPlan>) =>
    fetch(`/api/deployments/${reelId}/platforms/${platform}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}: ${await r.text()}`);
      return (await r.json()) as ReelDeployment;
    }),
  deploymentsDispatch: (reelId: string) =>
    post<DispatchResult>(`/api/deployments/${reelId}/dispatch`),
  deploymentsDelete: (reelId: string) => del<{ ok: boolean }>(`/api/deployments/${reelId}`),

  // ---- Capture (eigene Screenshots & Screen-Recordings direkt aus dem Studio) ----
  captureStatus: () => get<{ job: CaptureJob | null }>("/api/capture/status"),
  captureStart: (body: CaptureStartBody) => post<{ job: CaptureJob }>("/api/capture/start", body),
  captureStop: () => post<{ job: CaptureJob }>("/api/capture/stop"),
  captureCancel: () => post<{ job: CaptureJob }>("/api/capture/cancel"),
};

/* ---------- Capture types ---------- */
export type CaptureMode = "fullpage" | "viewport" | "screens" | "flow" | "video" | "manual";
export type CaptureResultFile = { name: string; url: string; kind: "image" | "video" | "other" };
export type CaptureJob = {
  id: string;
  mode: CaptureMode;
  url: string;
  device: string;
  out: string;
  status: "running" | "done" | "failed" | "cancelled";
  startedAt: number;
  finishedAt?: number;
  log: string[];
  results: CaptureResultFile[];
  recommendedScrollPct?: number;
  error?: string;
};
export type CaptureStartBody = {
  url: string;
  mode: CaptureMode;
  device?: string;
  out?: string;
  duration?: number;
  fps?: number;
  maxDuration?: number;
  scrollPct?: number;
  stops?: string;
  clicks?: string;
  width?: number;
  height?: number;
  cookie?: string;
  hide?: string;
  hideSticky?: boolean;
};
